import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { useGetPmCombinedReportQuery } from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import { formatDateLabel, formatRoleLabel } from "./reportingConfig";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const selectClass =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const Kpi = ({ label, value, tone = "text-gray-900" }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>
      {value ?? 0}
    </div>
  </div>
);

const PmCombined = () => {
  const navigate = useNavigate();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const [managerId, setManagerId] = useState("");
  // "" = the whole combined roll-up; otherwise a single team member.
  const [memberId, setMemberId] = useState("");

  const { data, isFetching } = useGetPmCombinedReportQuery({ managerId });
  const managers = data?.managers || [];
  const manager = data?.manager;
  const members = data?.members || [];

  const shownMembers = memberId
    ? members.filter((m) => String(m.employeeId) === memberId)
    : members;
  const shownUnits = useMemo(
    () =>
      (data?.units || []).filter(
        (u) => !memberId || String(u.employeeId) === memberId,
      ),
    [data, memberId],
  );
  const totals = useMemo(() => {
    if (!memberId) return data?.totals;
    return shownMembers.reduce(
      (a, m) => ({
        sheets: a.sheets + m.sheets,
        mainWorks: a.mainWorks + m.mainWorks,
        subWorks: a.subWorks + m.subWorks,
        completed: a.completed + m.completed,
        onHold: a.onHold + m.onHold,
        verified: a.verified + m.verified,
      }),
      { sheets: 0, mainWorks: 0, subWorks: 0, completed: 0, onHold: 0, verified: 0 },
    );
  }, [data, memberId, shownMembers]);

  return (
    <>
      <PageTitle activeMenu="PM Combined Report" motherMenu="Staff Reporting" />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Project Manager Combined Report
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                The Project Manager's work together with every Supervisor,
                Foreman and Civil Engineer under them. Pick a team member to see
                just their work.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {isSuperAdmin && (
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Project Manager
                  <select
                    className={`${selectClass} mt-1 block`}
                    value={managerId}
                    onChange={(e) => {
                      setManagerId(e.target.value);
                      setMemberId("");
                    }}
                  >
                    <option value="">Select project manager…</option>
                    {managers.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {manager && (
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  View
                  <select
                    className={`${selectClass} mt-1 block`}
                    value={memberId}
                    onChange={(e) => setMemberId(e.target.value)}
                  >
                    <option value="">Combined (everyone)</option>
                    {members.map((m) => (
                      <option key={m.employeeId} value={m.employeeId}>
                        {m.name} — {formatRoleLabel(m.role)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        </section>

        {!manager ? (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            {isFetching
              ? "Loading…"
              : isSuperAdmin
                ? "Select a project manager to see their combined report."
                : "No team found."}
          </section>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <Kpi label="Sheets" value={totals?.sheets} />
              <Kpi label="Main works" value={totals?.mainWorks} />
              <Kpi label="Sub-works" value={totals?.subWorks} />
              <Kpi label="Completed" value={totals?.completed} tone="text-emerald-600" />
              <Kpi label="On hold" value={totals?.onHold} tone="text-amber-600" />
              <Kpi label="Verified" value={totals?.verified} tone="text-sky-600" />
            </div>

            <section className={panel}>
              <div className="border-b border-gray-100 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Team — {manager.name}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-center">Sheets</th>
                      <th className="px-4 py-2 text-center">Main</th>
                      <th className="px-4 py-2 text-center">Sub</th>
                      <th className="px-4 py-2 text-center">Completed</th>
                      <th className="px-4 py-2 text-center">On hold</th>
                      <th className="px-4 py-2 text-center">Verified</th>
                      <th className="px-4 py-2 text-center">Last update</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shownMembers.map((m) => (
                      <tr key={m.employeeId} className="hover:bg-amber-50/30">
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {m.name}
                          {m.isManager && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              PM
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">{formatRoleLabel(m.role)}</td>
                        <td className="px-4 py-2 text-center tabular-nums">{m.sheets}</td>
                        <td className="px-4 py-2 text-center tabular-nums">{m.mainWorks}</td>
                        <td className="px-4 py-2 text-center tabular-nums">{m.subWorks}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-emerald-600">{m.completed}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-amber-600">{m.onHold}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-sky-600">{m.verified}</td>
                        <td className="px-4 py-2 text-center text-gray-500">
                          {m.lastEditedAt ? formatDateLabel(m.lastEditedAt) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:border-amber-400 hover:text-amber-700"
                            onClick={() => navigate(`/staff-reporting/employee/${m.employeeId}`)}
                          >
                            Individual reports
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className={panel}>
              <div className="border-b border-gray-100 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {memberId ? "Unit sheets" : "Combined unit sheets"} ({shownUnits.length})
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Unit</th>
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-center">Progress</th>
                      <th className="px-4 py-2 text-center">Main / Sub</th>
                      <th className="px-4 py-2 text-center">Completed</th>
                      <th className="px-4 py-2 text-center">On hold</th>
                      <th className="px-4 py-2 text-center">Verified</th>
                      <th className="px-4 py-2 text-center">Last update</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shownUnits.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                          No reports yet.
                        </td>
                      </tr>
                    )}
                    {shownUnits.map((u) => (
                      <tr key={u.reportId} className="hover:bg-amber-50/30">
                        <td className="px-4 py-2 font-medium text-gray-800">{u.unitName || "—"}</td>
                        <td className="px-4 py-2 text-gray-500">
                          {u.employeeName} · {formatRoleLabel(u.employeeRole)}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">{u.completionPercent}%</td>
                        <td className="px-4 py-2 text-center tabular-nums">{u.mainWorks} / {u.subWorks}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-emerald-600">{u.completed}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-amber-600">{u.onHold}</td>
                        <td className="px-4 py-2 text-center tabular-nums text-sky-600">{u.verified}</td>
                        <td className="px-4 py-2 text-center text-gray-500">{formatDateLabel(u.lastEditedAt)}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="rounded-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-amber-400"
                            onClick={() => navigate(`/staff-reporting/${u.reportId}`)}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
};

export default PmCombined;
